// glonet-react-tailwind-project--src--App.js
import React, { useState, useEffect, useCallback, Suspense, lazy, useRef } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Amplify, Auth, Hub } from "aws-amplify";
import { retryGoogleSignIn } from "./components/GoogleSignInButton";
import { bankReferral, readBanked, claimPayload, clearBanked } from "./services/referralBank";
import { UserProvider, useUser } from "./contexts/UserContext";
import { MessagingPhaseProvider } from './contexts/MessagingPhaseContext'; // ADDED THIS IMPORT
import awsconfig from "./config/aws-exports";
import NavBar from "./components/NavBar";
import TermsGate from "./components/TermsGate";
import LandingPage from "./components/LandingPage";
import GlonetAiLanding from "./components/GlonetAiLanding";
import HostsLanding from "./components/HostsLanding";
import WaterCoolerStory from "./components/WaterCoolerStory";
import AffiliatesLanding from "./components/AffiliatesLanding";
import AffiliateDashboard from "./components/AffiliateDashboard";
import HostDashboard from "./components/HostDashboard";
import OpsBrief from "./components/OpsBrief";
import AffiliatesStory from "./components/AffiliatesStory";
import HostsStory from "./components/HostsStory";
import ErrorBoundary from "./contexts/ErrorBoundary";
import log from "./utils/logger";
import api from "./services/api";
import { API_PATHS } from "./config/apiConfig";
import LegalPage from './components/LegalPage';


// Lazy load components
const Dashboard = lazy(() => import("./components/Dashboard"));
const AdventureGame = lazy(() => import("./components/AdventureGame/AdventureGame"));
const DMAdmin = lazy(() => import("./components/AdventureGame/DMAdmin"));
const AdventureAdmin = lazy(() => import("./components/AdventureGame/AdventureAdmin"));
const SimHarness = lazy(() => import("./components/AdventureGame/SimHarness"));
const AdvTestHarness = lazy(() => import("./components/AdventureGame/AdvTestHarness"));
const Replay = lazy(() => import("./orange/Replay"));
const InvestorWalkthrough = lazy(() => import("./investor-room/WalkthroughPage"));
const AdminInvestors = lazy(() => import("./investor-room/AdminInvestors"));
const ReplayTestHarness = lazy(() => import("./orange/ReplayTestHarness"));
const OrangeDevPage = lazy(() => import("./orange/DevPage"));
const VCSocialScore = lazy(() => import("./vcsocialscore/VCSocialScore"));
const VcsTestHarness = lazy(() => import("./vcsocialscore/VcsTestHarness"));
const SignIn = lazy(() => import("./components/SignIn"));
const SignUp = lazy(() => import("./components/SignUp"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const VerifyCode = lazy(() => import("./components/VerifyCode"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const Events = lazy(() => import("./components/Events"));
const CreateEvent = lazy(() => import("./components/CreateEvent"));
const EventDetails = lazy(() => import("./components/EventDetails"));
const InvitationHandler = lazy(() => import("./components/InvitationHandler"));
const AttendeeForm = lazy(() => import("./components/AttendeeForm"));
const MatchesDashboard = lazy(() => import("./components/MatchesDashboard"));
const Feedback = lazy(() => import("./components/Feedback"));
const MatchPricingTiers = lazy(() => import("./components/MatchPricingTiers"));
const ConnectAccountSetup = lazy(() => import("./components/ConnectAccountSetup"));
const StripeConnectSuccess = lazy(() => import("./components/StripeConnectSuccess"));
const AttendeeEventPricing = lazy(() => import("./components/AttendeeEventPricing"));
// Subscription pricing flow (pricing_mode gate — docs in glonet-multiple-lambdas/docs/subscription-pricing/)
const SubscriptionPricing = lazy(() => import("./components/SubscriptionPricing"));
const ManageSubscription = lazy(() => import("./components/ManageSubscription"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const GlonetEvents = lazy(() => import("./components/GlonetEvents"));
const HostEventsPage = lazy(() => import("./components/HostEventsPage"));
const MatchingAnimation = lazy(() => import("./components/MatchingAnimation"));
const MatchingAnimation2 = lazy(() => import("./components/MatchingAnimation2"));
const AffiliateOnboardingHandler = lazy(() => import("./components/AffiliateOnboardingHandler"));
const ManageAffiliates = lazy(() => import("./components/ManageAffiliates"));
const RoomTicketPage = lazy(() => import("./components/RoomTicketPage")); // private room tickets buy page
const ShoutoutPage = lazy(() => import("./components/ShoutoutPage")); // emailed shout-out ask lands here
const AgentConversationHandler = lazy(() => import("./components/AgentConversation/AgentConversationHandler"));
const WaterCoolerGate = lazy(() => import("./components/WaterCoolerGate"));
const WCTestHarness = lazy(() => import("./components/WaterCooler/WCTestHarness")); // dev-only route
const WCGateTestHarness = lazy(() => import("./components/WaterCooler/WCGateTestHarness")); // dev-only route
const AdminTestHarness = lazy(() => import("./components/admin/AdminTestHarness")); // dev-only admin fixture
const OpsTestHarness = lazy(() => import("./components/admin/OpsTestHarness")); // dev-only /ops + host-dashboard fixture
const TaglineConsentHarness = lazy(() => import("./components/WaterCooler/TaglineConsentHarness")); // dev-only screenshot fixture
const GameTestHarness = lazy(() => import("./components/WaterCooler/arcade/GameTestHarness")); // dev-only game harness
const MiniTestHarness = lazy(() => import("./components/WaterCooler/arcade/MiniTestHarness")); // dev-only spectator-window harness
const BoardroomHarness = lazy(() => import("./components/WaterCooler/rooms/BoardroomHarness")); // dev-only boardroom fixture
const WCTemplateStudio = lazy(() => import("./components/WaterCooler/templates/TemplateStudio")); // admin-only preview
const TechWeekPage = lazy(() => import("./techweek/TechWeekPage")); // SF Tech Week planner (full-bleed)
const TwTestHarness = lazy(() => import("./techweek/TwTestHarness")); // dev-only techweek fixture
// Shareable read-only studio for prospective hosts/affiliates — email-gated
// against the wc_studio_access allowlist (Admin → Hosts). No auth required.
const StudioGate = lazy(() => import("./components/WaterCooler/templates/StudioPreview")
  .then((m) => ({ default: m.StudioGate })));
const StudioPreview = lazy(() => import("./components/WaterCooler/templates/StudioPreview")
  .then((m) => ({ default: m.StudioPreview })));


if (process.env.NODE_ENV !== "production") {
  log.setLevel("debug");
}

Amplify.configure(awsconfig);

// Wrap account-only routes: signed-out visitors go to sign-in (carrying `from`
// so they come back), instead of seeing a broken empty page.
function RequireAuth({ children }) {
  const [status, setStatus] = useState("checking");
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;
    Auth.currentAuthenticatedUser()
      .then(() => { if (!cancelled) setStatus("ok"); })
      .catch(() => { if (!cancelled) setStatus("anon"); });
    return () => { cancelled = true; };
  }, [location.pathname]);
  if (status === "checking") return <div>Loading...</div>;
  if (status === "anon") {
    return <Navigate to="/signin" state={{ from: location.pathname + location.search }} replace />;
  }
  return children;
}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [blockMessage, setBlockMessage] = useState(null);
  const { refreshUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // ——— Google sign-in (Cognito hosted OAuth) ———
  // The redirect wipes SPA state: customState carries the funnel context
  // (from/invitationCode/eventId) and is replayed here. A first Google
  // sign-in on an email that already has a password account is aborted by
  // the preSignup lambda AFTER linking the identities — the marker in the
  // error triggers exactly one silent retry, which then rides the link into
  // the original account.
  useEffect(() => {
    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn") {
        sessionStorage.removeItem("g-signin-retry");
        checkAuthState();
      } else if (payload.event === "customOAuthState" && payload.data) {
        try {
          const { from, invitationCode, eventId } = JSON.parse(payload.data);
          if (from) navigate(from, { state: { invitationCode, eventId }, replace: true });
        } catch (e) { /* malformed state — land on the default page */ }
      } else if (payload.event === "signIn_failure") {
        const msg = String((payload.data && payload.data.message) || payload.data || "");
        if (msg.includes("GLONET_LINKED_RETRY") && !sessionStorage.getItem("g-signin-retry")) {
          sessionStorage.setItem("g-signin-retry", "1");
          retryGoogleSignIn(); // carries the original return address
        }
      }
    });
    // the abort can also surface as ?error_description=... on the redirect URL
    const q = new URLSearchParams(window.location.search);
    const desc = q.get("error_description") || "";
    if (desc.includes("GLONET_LINKED_RETRY") && !sessionStorage.getItem("g-signin-retry")) {
      sessionStorage.setItem("g-signin-retry", "1");
      retryGoogleSignIn();
    }
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthState = useCallback(async () => {
    try {
      await Auth.currentAuthenticatedUser();
      
      // Additional check to see if user is blocked
      try {
        await api.get(API_PATHS.USERS.PROFILE);
        setIsAuthenticated(true);
      } catch (apiErr) {
        // Check if error is due to blocked account
        if (apiErr.response && apiErr.response.status === 403 && 
            apiErr.response.data && apiErr.response.data.detail && 
            apiErr.response.data.detail.includes("Access denied")) {
          
          // Store block message
          setBlockMessage(apiErr.response.data.detail);
          
          // Force sign out
          try {
            await Auth.signOut();
            setIsAuthenticated(false);
            navigate("/");
          } catch (signOutErr) {
            log.error("Error signing out blocked user", { error: signOutErr });
          }
        } else {
          // Some other API error, but user is authenticated with Cognito
          setIsAuthenticated(true);
        }
      }
    } catch {
      setIsAuthenticated(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  // G1 (cold start): an ?affiliate_code= / ?ref= on ANY page is banked in
  // localStorage, and once the visitor is signed in it binds server-side via
  // POST users/referral (first code wins, self-referral refused). A free
  // founding signup therefore keeps its affiliate until conversion — checkout
  // falls back to users.signup_affiliate_code when no code rides the request.
  // Owner rulings 2026-09-21 (services/referralBank.js): the FIRST link this
  // browser saw is the one that counts; later links ride along only to be
  // logged, so a referrer who says "I gave them my link" can be answered.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("affiliate_code") || params.get("ref");
    // ?p=<post_id> rides alongside ?ref= (shout-outs, 2026-08-26): WHICH post
    // brought this visitor. Money is untouched — the code still owns
    // attribution — but without this the shout-out engine can only ever be
    // believed, never measured against posts nobody boosted.
    if (code) bankReferral(code, params.get("p"));
  }, [location.search]);
  const referralInFlight = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || referralInFlight.current) return;
    const payload = claimPayload(readBanked());
    if (!payload) return;
    referralInFlight.current = true;
    api.post("users/referral", payload)
      .then(() => clearBanked())
      .catch(() => { /* transient — retried on the next sign-in or link */ })
      .finally(() => { referralInFlight.current = false; });
    // location.search too: a member who is ALREADY signed in and opens a
    // referral link gets it logged now, not at their next sign-in
  }, [isAuthenticated, location.search]);

  // glonet.ai landing: served full-bleed (no NavBar / app container) either on the
  // glonet.ai domain root, or at /glonet-ai on any domain (preview before DNS cutover).
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const onGlonetAiHost = host === "glonet.ai" || host === "www.glonet.ai";
  // Trailing slash normalization. These full-bleed pages are matched by exact
  // string, so "/affiliates/" used to miss every branch below and fall through
  // to the /:handle catch-all — which rendered "Page not found" on LIVE
  // marketing URLs (glonet.ai/affiliates/ and glonet.ai/hosts/ both 404'd in
  // production). Found by the happy-path flow test, 2026-07-29.
  const path = location.pathname.length > 1
    ? location.pathname.replace(/\/+$/, "")
    : location.pathname;
  const showGlonetLanding =
    path === "/glonet-ai" ||
    (onGlonetAiHost && path === "/");
  if (showGlonetLanding) {
    return <GlonetAiLanding />;
  }
  // /hosts: public hosts/affiliates marketing page — full-bleed sibling of the
  // glonet.ai landing (its own minimal nav), reachable on every domain. Served
  // here rather than as a Route so it skips the app NavBar/container, and so
  // the /:handle catch-all route can never claim it.
  if (path === "/hosts") {
    return <HostsLanding />;
  }
  // /techweek: the SF Tech Week planner (2026-10-01) — anonymous funnel page
  // over the 1,721-event calendar; saving a week is the sign-up. Full-bleed
  // for the same reasons as /hosts, and kept out of the /:handle catch-all's
  // reach. Backend-gated by wc_settings.techweek_enabled (page shows its own
  // "wrapped" state after the week). Touches nothing in the landing funnel.
  if (path === "/techweek") {
    return (
      <Suspense fallback={null}>
        <TechWeekPage />
      </Suspense>
    );
  }
  if (process.env.NODE_ENV !== "production" && path === "/__tw-test") {
    return (
      <Suspense fallback={null}>
        <TwTestHarness />
      </Suspense>
    );
  }
  // /hosts/dashboard: the host's own members and money (AI Ops item 4). Same
  // full-bleed treatment as the affiliate dashboard, and served here rather
  // than as a Route so the /:handle catch-all can never claim it.
  if (path === "/hosts/dashboard") {
    return <HostDashboard />;
  }
  // /ops: the voice agent that runs the day. Admin-gated by the endpoint it
  // reads (a non-admin gets the "this one's for the team" state), full-bleed
  // because it is a single-purpose surface, not part of the app chrome.
  if (path === "/ops") {
    return <OpsBrief />;
  }
  // The offline harness for the two screens above is served here TOO, not as a
  // Route, so it inherits the same full-bleed treatment. Served from a Route it
  // rendered inside the app NavBar, which meant every screenshot showed chrome
  // production does not have — a fixture that lies about the thing it is
  // photographing is worse than no fixture.
  if (process.env.NODE_ENV !== "production" && path === "/__ops-test") {
    return (
      <Suspense fallback={null}>
        <OpsTestHarness />
      </Suspense>
    );
  }
  // /watercooler-story: "a virtual meeting space" — the Water Cooler explainer
  // moved off the one-screen landing; linked from the ⓘ line under the hero
  // button. NOT /watercooler — that's the room itself.
  if (path === "/watercooler-story") {
    return <WaterCoolerStory />;
  }
  // /affiliates: the founding-affiliate recruiting page, same full-bleed
  // treatment. /affiliates/story and /hosts/story are the narrated overtures
  // (StoryOverture, investor-room engine) that land on their pages.
  if (path === "/affiliates") {
    return <AffiliatesLanding />;
  }
  // /affiliates/dashboard is the affiliate's home (Today/Outreach/Messages/
  // Results, 2026-08-12); /affiliates/stats is its old name and lands there
  // too so every emailed link keeps working.
  if (path === "/affiliates/dashboard" || path === "/affiliates/stats") {
    return <AffiliateDashboard />;
  }
  if (path === "/affiliates/story") {
    return <AffiliatesStory />;
  }
  if (path === "/hosts/story") {
    return <HostsStory />;
  }
  // /room-ticket/<ROOMCODE>: buy page + Stripe return for private room
  // tickets (2026-08-24). Full-bleed like the affiliate pages, and served
  // here so the /:handle catch-all can never claim it. The room code is what
  // affiliates promote, so this URL is in their posts and emails.
  if (location.pathname.startsWith("/room-ticket/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/room-ticket/:roomCode" element={<RoomTicketPage />} />
        </Routes>
      </Suspense>
    );
  }
  // /shoutout/<token>: where an emailed shout-out ask lands (2026-08-26).
  // PUBLIC and full-bleed — the token is the credential, so a booster answers
  // from their phone mail client without signing in. Served here rather than as
  // a Route so it skips the NavBar and the /:handle catch-all can never claim it.
  if (location.pathname.startsWith("/shoutout/")) {
    return (
      <Suspense fallback={null}>
        <ShoutoutPage />
      </Suspense>
    );
  }
  // /investors/<code>: the MML Investor Room walkthrough + Gallery — public,
  // full-bleed, credentialed by the invite code alone (src/investor-room/).
  // Served here so it skips the NavBar/container and the /:handle catch-all.
  if (location.pathname.startsWith("/investors/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/investors/:code" element={<InvestorWalkthrough />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="App min-h-screen"
         style={{ background: "var(--ga-bg)", color: "var(--ga-text)",
                  fontFamily: "'Hanken Grotesk',system-ui,sans-serif" }}>
      <NavBar
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
      />

      {/* Asks once, and only from members with no acceptance on file — the
          Google buttons create accounts without passing the signup form. */}
      <TermsGate />

      {/* Show block message if present */}
      {blockMessage && (
        <div className="max-w-md mx-auto mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <h3 className="text-lg font-semibold">Account Suspended</h3>
          <p>{blockMessage}</p>
          <p className="mt-2 text-sm">
            If you believe this is an error, please contact bhartsb@gmail.com.
          </p>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
          <Route path="/matching-animation2" element={<MatchingAnimation2 />} />
          <Route path="/matching-animation" element={<MatchingAnimation />} />
          <Route path="/nexuses" element={<RequireAuth><GlonetEvents /></RequireAuth>} />
          <Route path="/watercooler" element={<WaterCoolerGate />} />
          <Route path="/adventure" element={<RequireAuth><AdventureGame /></RequireAuth>} />
          <Route path="/adventure/dm" element={<RequireAuth><DMAdmin /></RequireAuth>} />
          <Route path="/adventure/admin" element={<RequireAuth><AdventureAdmin /></RequireAuth>} />
          <Route path="/adventure/sim" element={<RequireAuth><SimHarness /></RequireAuth>} />
          <Route path="/orange" element={<RequireAuth><Replay /></RequireAuth>} />
          <Route path="/orange/admin" element={<RequireAuth><Replay initialScreen="admin" /></RequireAuth>} />
          <Route path="/orange/devs" element={<OrangeDevPage />} />
          <Route path="/replay" element={<Navigate to="/orange" replace />} />
          {/* Public teaser tier: reading the directory needs no account. The
              shell probes the session itself and drops to /public/* when there
              is none. Writing (rate) and moderation (admin) stay gated. */}
          <Route path="/vcsocialscore" element={<VCSocialScore />} />
          <Route path="/vcsocialscore/funds" element={<VCSocialScore initialScreen="funds" />} />
          <Route path="/vcsocialscore/funds/:id" element={<VCSocialScore initialScreen="fund" />} />
          <Route path="/vcsocialscore/people" element={<VCSocialScore initialScreen="people" />} />
          <Route path="/vcsocialscore/people/:id" element={<VCSocialScore initialScreen="person" />} />
          <Route path="/vcsocialscore/policy" element={<VCSocialScore initialScreen="policy" />} />
          <Route path="/vcsocialscore/forum" element={<VCSocialScore initialScreen="forum" />} />
          <Route path="/vcsocialscore/forum/:id" element={<VCSocialScore initialScreen="thread" />} />
          <Route path="/vcsocialscore/rate" element={<RequireAuth><VCSocialScore initialScreen="rate" publicMode={false} /></RequireAuth>} />
          <Route path="/vcsocialscore/admin" element={<RequireAuth><VCSocialScore initialScreen="admin" publicMode={false} /></RequireAuth>} />
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__vcs-test" element={<VcsTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__replay-test" element={<ReplayTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__adv-test" element={<AdvTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__wc-test" element={<WCTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__wc-gate-test" element={<WCGateTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__admin-test" element={<AdminTestHarness />} />
          )}
          <Route path="/admin/wc-templates" element={<RequireAuth><WCTemplateStudio /></RequireAuth>} />
          {/* Public, email-gated preview of the same studio — the link the
              owner shares with prospective hosts and affiliates. MUST stay
              above the /:handle catch-all below or it renders a host page. */}
          <Route path="/water-cooler-studio" element={<StudioGate />} />
          <Route path="/water-cooler-studio/preview" element={<StudioPreview />} />
          <Route path="/admin/investors" element={<RequireAuth><AdminInvestors /></RequireAuth>} />
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__wc-templates" element={<WCTemplateStudio />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__tagline-test" element={<TaglineConsentHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__game-test" element={<GameTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__mini-test" element={<MiniTestHarness />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__boardroom-test" element={<BoardroomHarness />} />
          )}
          <Route path="/agent-conversations/:conversationId" element={<AgentConversationHandler />} />
          <Route path="/:handle" element={<HostEventsPage />} />
            <Route
              path="/"
              element={isAuthenticated ? <Dashboard /> : <LandingPage />}
            />
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route
              path="/signin"
              element={<SignIn setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route
              path="/signup"
              element={<SignUp setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route
              path="/verify-code"
              element={<VerifyCode setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/profile"
              element={<RequireAuth><UserProfile setIsAuthenticated={setIsAuthenticated} /></RequireAuth>}
            />
            <Route path="/events" element={<RequireAuth><Events /></RequireAuth>} />
            <Route path="/events/create" element={<RequireAuth><CreateEvent /></RequireAuth>} />
            <Route path="/events/:eventId" element={<EventDetails />} />
            <Route path="/events/pricing-tiers" element={<MatchPricingTiers />} />
            <Route
              path="/:eventId/:invitationCode/register" // Keep original path-based invite
              element={<InvitationHandler />}
            />
            <Route
              path="/events/:eventId/register" // Direct registration, or with ?affiliate_code
              element={<InvitationHandler />}
            />
            <Route path="/feedback" element={<Feedback />} />
            <Route
              path="/events/:eventId/attendees"
              element={<AttendeeForm />}
            />
            <Route
              path="/events/:eventId/matches"
              element={<RequireAuth><MatchesDashboard /></RequireAuth>}
            />
            <Route path="/events/connect-setup" element={<RequireAuth><ConnectAccountSetup /></RequireAuth>} />
            <Route path="/events/stripe-success" element={<StripeConnectSuccess />} />
            <Route path="/events/:eventId/pricing" element={<AttendeeEventPricing />} />
            {/* Subscription pricing flow (tiers with pricing_mode=subscription) */}
            <Route path="/events/:eventId/subscribe" element={<RequireAuth><SubscriptionPricing /></RequireAuth>} />
            <Route path="/events/:eventId/subscription" element={<RequireAuth><ManageSubscription /></RequireAuth>} />
            {/* Admin dashboard (backend enforces users.is_admin) */}
            <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
            {/* Add Legal Routes */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/:tab" element={<LegalPage />} />
            <Route path="/affiliate-onboarding/:affiliateCode" element={<AffiliateOnboardingHandler />} />
            <Route path="/events/:eventId/manage-affiliates" element={<RequireAuth><ManageAffiliates /></RequireAuth>} /> 
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    const t0 = performance.now();

    return () => {
      const t1 = performance.now();
      console.log(`App took ${t1 - t0} milliseconds to render`);
    };
  }, []);

  return (
    <ErrorBoundary>
      <UserProvider>
        <MessagingPhaseProvider>
          <AppContent />
        </MessagingPhaseProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;